import { FAILED, MISSED, PENDING, SUCCESS, SUSPENDED, UNKNOWN } from './passnflyStatuses';
import { getCheckInStatusTextKeys } from './constants';

/**
 * This method returns the check-in status information of a passenger.
 * -- In case the response from passNFly returns a 'REGISTRATION_NOT_FOUND' errorCode, the method returns:
 *     -- FAILED - for the segments that depart in less than the check-in window timeframe.
 *     -- PENDING - for the past segments or the segments that depart in more than the check-in window timeframe.
 * -- In case the response from passNFly returns a 'GENERIC_ERROR' errorCode, the method returns UNKNOWN.
 * -- In case of no error, the method returns:
 *     -- the check-in status of the given passenger and segment, if the passenger and the segment are found in the response from passNFly
 *     -- UNKNOWN, if the passenger and the segment are not found in the response from passNFly,
 */
const getPassNFlyPassengerStatus = (checkInStatus, checkInTimeframe, segment, traveler) => {
  if (checkInStatus.errorCode === 'REGISTRATION_NOT_FOUND') {
    const today = new Date();
    const hoursLeftUntilDeparture = (new Date(segment.departuredAt) - today) / (60 * 60 * 1000);
    return hoursLeftUntilDeparture <= 0 || hoursLeftUntilDeparture < checkInTimeframe
      ? FAILED
      : PENDING;
  }

  if (checkInStatus.errorCode === 'GENERIC_ERROR') {
    return UNKNOWN;
  }

  const passNFlyPassenger = checkInStatus.passengers.find(
    (passenger) => traveler.name === `${passenger.firstName} ${passenger.lastName}`,
  );
  const passNFlyLeg = checkInStatus.legs.find(
    (leg) =>
      leg.departureAirport === segment.origin.code &&
      leg.arrivalAirport === segment.destination.code,
  );
  const passNFlyPassengerStatus = passNFlyLeg?.passengerLegs?.find(
    ({ passengerId }) => passNFlyPassenger?.passengerId === passengerId,
  );

  return passNFlyPassengerStatus?.status || UNKNOWN;
};

const isSegmentInTheFutureOrToday = (segment, today) => new Date(segment.arrivedAt) >= today;

export const isBoundInThePast = (bound, today) =>
  !isSegmentInTheFutureOrToday(bound.segments[bound.segments.length - 1], today);

const getCheckInStatusOfSegmentPerPassenger = ({
  checkInStatus,
  checkInTimeframe,
  segment,
  travelers,
}) =>
  travelers.map((traveler) => ({
    fullName: traveler.name,
    passengerId: traveler.id,
    status: getPassNFlyPassengerStatus(checkInStatus, checkInTimeframe, segment, traveler),
  }));

/**
 * This method calculates if a given bound is upcoming.
 * A bound is considered as upcoming, if it is the first of the future bounds or if it departs in less than the check-in window timeframe
 */
const isBoundUpcoming = (bound, hasUpcomingBounds, today, checkInExpirationDate) => {
  if (hasUpcomingBounds) {
    return new Date(bound.segments[0].departuredAt) <= checkInExpirationDate;
  }

  const futureSegments = bound.segments.filter((segment) =>
    isSegmentInTheFutureOrToday(segment, today),
  );
  return futureSegments.length > 0;
};

export const getFormattedBounds = (bounds, checkInStatus, checkInTimeframe, travelers) => {
  const today = new Date();
  const checkInExpirationDate = new Date();
  checkInExpirationDate.setHours(today.getHours() + checkInTimeframe);
  let isUpcoming = false;
  let hasUpcomingBounds = false;

  return bounds.map((bound) => {
    isUpcoming = isBoundUpcoming(bound, hasUpcomingBounds, today, checkInExpirationDate);

    if (isUpcoming) {
      hasUpcomingBounds = true;
    }

    return {
      ...bound,
      segments: bound.segments.reduce((acc, segment) => {
        if (segment.__typename === 'TripSegment') {
          acc.push({
            ...segment,
            checkInStatusPerPassenger: getCheckInStatusOfSegmentPerPassenger({
              checkInStatus,
              segment,
              checkInTimeframe,
              travelers,
            }),
          });
        }
        return acc;
      }, []),
      isUpcoming,
    };
  });
};

const getUpcomingBoundStatuses = (bounds) => {
  return [
    ...new Set(
      bounds.flatMap((bound) =>
        bound.segments.flatMap(({ checkInStatusPerPassenger }) =>
          bound.isUpcoming ? checkInStatusPerPassenger.map(({ status }) => status) : [],
        ),
      ),
    ),
  ];
};

const getBoundStatus = (bounds, statusTextAllBounds, statusTextUpcomingBound) => {
  return bounds.length === 1 || bounds[bounds.length - 1].isUpcoming
    ? statusTextAllBounds
    : statusTextUpcomingBound;
};

const getBoundPendingStatus = (bounds, statusTextAllBounds, statusTextUpcomingBound) => {
  const today = new Date();
  const isEveryBoundInTheFuture = !bounds.some((bound) => isBoundInThePast(bound, today));
  return bounds.length === 1 || isEveryBoundInTheFuture
    ? statusTextAllBounds
    : statusTextUpcomingBound;
};

export const getCheckInStatusContent = (bounds, type) => {
  const upcomingBoundStatuses = getUpcomingBoundStatuses(bounds);
  const FAILING_STATUSES = [FAILED, MISSED, SUSPENDED];
  const STATUS_TEXT_KEYS = getCheckInStatusTextKeys(type);

  if (upcomingBoundStatuses.length === 0) {
    return null;
  }

  if (upcomingBoundStatuses.every((status) => FAILING_STATUSES.includes(status))) {
    return getBoundStatus(
      bounds,
      STATUS_TEXT_KEYS.FAILED.ALL_BOUNDS,
      STATUS_TEXT_KEYS.FAILED.UPCOMING_BOUND,
    );
  }

  if (upcomingBoundStatuses.every((status) => status === SUCCESS)) {
    return getBoundStatus(
      bounds,
      STATUS_TEXT_KEYS.SUCCESS.ALL_BOUNDS,
      STATUS_TEXT_KEYS.SUCCESS.UPCOMING_BOUND,
    );
  }

  if (upcomingBoundStatuses.every((status) => status === PENDING)) {
    return getBoundPendingStatus(
      bounds,
      STATUS_TEXT_KEYS.PENDING.ALL_BOUNDS,
      STATUS_TEXT_KEYS.PENDING.UPCOMING_BOUND,
    );
  }

  if (FAILING_STATUSES.some((status) => upcomingBoundStatuses.includes(status))) {
    return getBoundStatus(
      bounds,
      STATUS_TEXT_KEYS.PARTIALLY_FAILED.ALL_BOUNDS,
      STATUS_TEXT_KEYS.PARTIALLY_FAILED.UPCOMING_BOUND,
    );
  }

  if (upcomingBoundStatuses.includes(UNKNOWN)) {
    return STATUS_TEXT_KEYS.UNKNOWN;
  }

  return getBoundStatus(
    bounds,
    STATUS_TEXT_KEYS.PENDING.ALL_BOUNDS,
    STATUS_TEXT_KEYS.PENDING.UPCOMING_BOUND,
  );
};

export const isStatusSameForAllPassengersOfSegment = (segment) =>
  segment.checkInStatusPerPassenger.every(
    ({ status }) => status === segment.checkInStatusPerPassenger[0].status,
  );
